import taxPeriod from 'assets/files/tax-period.pdf'
import kaspiTemplate from 'assets/files/Выписка Kaspi Gold .pdf'
import kaspiTemplatePhys from 'assets/files/Выписка Kaspi по ИП.pdf'
import KaspiBankIcon from 'assets/icons/kaspi-bank-icon.svg?component'
import WarningIcon from 'assets/icons/warning-icon.svg?component'
import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import FileChip from 'components/UI/Chips/FileChip'
import DocumentUpload from 'components/UI/DocumentUpload'
import FileInput from 'components/UI/Inputs/FileInput'
import { Modal } from 'components/UI/Modal'
import Typography from 'components/UI/Typography'
import { ChangeEvent, Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Iframe from 'react-iframe'
import { UploadAdditionalDocsResponse } from 'share/types/api/main'
import { UpgradeConditionsFormFiles } from 'share/types/model/files'
import tw from 'twin.macro'
import { UpdateConditionsModalTexts } from 'utils/constants'

type UpgradeConditionsFormProps = {
  form: {
    [key in UpgradeConditionsFormFiles]: {
      maxFiles: number
      files: Array<File>
    }
  }
  errors: UploadAdditionalDocsResponse
  isLoading: boolean
  isStepInError: boolean
  onFileChange: ({ name, file }: { name: string; file?: File }) => void
  onFileRemove: ({ name, file }: { name: string; file?: File }) => void
}

const UpgradeConditionsForm = ({
  form,
  isLoading = false,
  isStepInError = false,
  errors,
  onFileChange,
  onFileRemove
}: UpgradeConditionsFormProps) => {
  //Tools
  const { t } = useTranslation()
  // State
  const [modalState, setModalState] = useState<{ open: boolean; value: string | number | undefined }>({
    open: false,
    value: undefined
  })

  const [modalTemplate, setModalTemplate] = useState<{ open: boolean; doc: string | undefined }>({
    open: false,
    doc: undefined
  })

  // Handlers
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e) return
    const { name, files } = e.target

    if (files && files.length !== 0) {
      const inputFile = files[0]
      const fieldForm = form[name as UpgradeConditionsFormFiles]
      if (fieldForm.files.find(file => file.name === inputFile.name || fieldForm.maxFiles === fieldForm.files.length))
        return
      onFileChange({ name, file: files[0] })
    }
  }

  const handleFileRemove = ({ name, file }: { name: string; file: File }) => {
    onFileRemove({ name, file })
  }

  const handleModalStateChange = (value: string | number | undefined) => {
    setModalState({
      open: true,
      value: value
    })
  }

  const handleModalClose = () => {
    setModalState(prevState => ({
      ...prevState,
      open: false
    }))
  }
  const handleModalTempalteClose = () => {
    setModalTemplate(prevState => ({
      ...prevState,
      open: false
    }))
  }

  return (
    <Fragment>
      <InfoBlock twStyle={tw`p-0 mt-4`}>
        {isStepInError && (
          <div tw='px-5 pt-5'>
            <Alert icon={<WarningIcon tw='text-red-light flex-shrink-0' />}>
              <Typography variant='subbody'>{t('flow-doc-check-failed')}</Typography>
            </Alert>
          </div>
        )}
        <DocumentUpload
          title={
            <div>
              <Typography variant='body' weight='bold'>
                {t('flow-tax-form')}
              </Typography>
              <Typography variant='caption' twStyle={tw`mt-1 text-grey-dark`}>
                {t('flow-sure-template')}
              </Typography>
              <button
                tw='mt-3 text-[11px] flex gap-1 items-center text-[var(--grey-dark)] underline'
                onClick={() => {
                  setModalTemplate({
                    open: true,
                    doc: taxPeriod
                  })
                }}
              >
                {t('flow-view-template')}
              </button>
            </div>
          }
          modalName='TAX_FORM'
          onModalIconClick={handleModalStateChange}
        >
          <div tw='flex items-center gap-4'>
            <FileInput
              name='PARSE_SIMPLIFIED'
              twStyle={tw`mt-5`}
              disabled={form['PARSE_SIMPLIFIED'].files.length === form['PARSE_SIMPLIFIED'].maxFiles || isLoading}
              onChange={e => handleFileChange(e)}
            />
            {errors?.parse_simplified && (
              <Typography variant='caption' twStyle={tw`text-red-light mt-1`}>
                {errors.parse_simplified[0]}
              </Typography>
            )}
          </div>
          <div tw='flex flex-wrap mt-1'>
            {form['PARSE_SIMPLIFIED'].files.map(file => (
              <FileChip
                key={file.name}
                title={file.name}
                onRemoveChip={() => handleFileRemove({ name: 'PARSE_SIMPLIFIED', file })}
              />
            ))}
          </div>
        </DocumentUpload>
        <hr tw='mx-5 h-px' />
        <DocumentUpload
          modalName='BVU'
          title={
            <div>
              <Typography variant='body' weight='bold'>
                {t('flow-bvu')}
              </Typography>
              <Typography variant='caption' twStyle={tw`mt-1 text-grey-dark`}>
                {t('flow-bvu-upload')}
              </Typography>

              <div tw='flex flex-wrap items-center gap-x-4 gap-y-2 mt-2'>
                <KaspiBankIcon />
              </div>
              <button
                tw='mt-3 text-[11px] flex gap-1 items-center text-[var(--grey-dark)] underline'
                onClick={() => {
                  setModalTemplate({
                    open: true,
                    doc: kaspiTemplatePhys
                  })
                }}
              >
                {t('flow-view-template-ur')}
              </button>
              <button
                tw='mt-1 text-[11px] flex gap-1 items-center text-[var(--grey-dark)] underline'
                onClick={() => {
                  setModalTemplate({
                    open: true,
                    doc: kaspiTemplate
                  })
                }}
              >
                {t('flow-view-template-phys')}
              </button>
            </div>
          }
          onModalIconClick={handleModalStateChange}
        >
          <FileInput
            name='BANK_STATEMENT'
            twStyle={tw`mt-5`}
            disabled={form['BANK_STATEMENT'].files.length === form['BANK_STATEMENT'].maxFiles || isLoading}
            onChange={e => handleFileChange(e)}
          />
          {errors?.bank_statement && (
            <Typography variant='caption' twStyle={tw`text-red-light mt-1`}>
              {errors.bank_statement[0]}
            </Typography>
          )}
          <div tw='flex flex-wrap mt-1'>
            {form['BANK_STATEMENT'].files.map(file => (
              <FileChip
                key={file.name}
                title={file.name}
                onRemoveChip={() => handleFileRemove({ name: 'BANK_STATEMENT', file })}
              />
            ))}
          </div>
          <Typography variant='subbody' twStyle={tw`inline-flex items-center space-x-2 text-grey-dark mt-2`}>
            <WarningIcon tw='flex-shrink-0 text-[#FBD655] ml-[2px]' />
            <span>{t('flow-file-type')}</span>
          </Typography>
        </DocumentUpload>
        {/* TODO: Вернуть оборотно сальдовую потом */}
        {/* <hr tw='mx-5 h-px' />
          <DocumentUpload
            modalName='OBV'
            title={
              <div>
                <Typography variant='body' weight='bold'>
                  Оборотно-сальдовая ведомость
                </Typography>
                <Typography variant='caption' twStyle={tw`mt-0.5 text-grey-dark`}>
                  Загрузить оборотно-сальдовую ведомость в формате .mxl из 1С-Бухгалтерия
                </Typography>
              </div>
            }
            onModalIconClick={handleModalStateChange}
          >
            <FileInput
              name='TURNOVER_BALANCE'
              twStyle={tw`mt-5`}
              disabled={form['TURNOVER_BALANCE'].files.length === form['TURNOVER_BALANCE'].maxFiles}
              onChange={e => handleFileChange(e)}
            />
            <div tw='flex flex-wrap mt-1'>
              {form['TURNOVER_BALANCE'].files.map(file => (
                <FileChip
                  key={file.name}
                  title={file.name}
                  onRemoveChip={() => handleFileRemove({ name: 'TURNOVER_BALANCE', file })}
                />
              ))}
            </div>
          </DocumentUpload> 
        */}
        <Modal
          title={''}
          open={modalTemplate.open}
          onClose={handleModalTempalteClose}
          wrapperStyle={tw`overflow-hidden`}
          twStyle={tw`min-w-[788px] p-5`}
        >
          <Iframe width='100%' tw='min-h-[80vh] py-5' url={modalTemplate.doc as string} />
        </Modal>
        <Modal
          title={modalState.value ? UpdateConditionsModalTexts[modalState.value].title : ''}
          open={modalState.open}
          onClose={handleModalClose}
          wrapperStyle={tw`overflow-hidden`}
          twStyle={tw`max-w-[788px]`}
        >
          <Typography twStyle={tw`leading-7 tracking-[-0.24px] mt-6`}>
            {modalState.value ? UpdateConditionsModalTexts[modalState.value].text : ''}
          </Typography>
        </Modal>
      </InfoBlock>
    </Fragment>
  )
}

export default UpgradeConditionsForm
